import { AxiosResponse } from 'axios'
import chunk from 'lodash/chunk'

import { Organisation, Partner } from '../admin/admin.types'
import { Donation, DonationEnhanced } from '../donations/donations.types'

import { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import {
  disbursementsServiceCapabilities,
  DisbursementBatch,
  GetDisbursementBatchesListProps,
  DisbursementBatchWithCancellationFailures,
  CreateDisbursementBatchProps,
  GetDisbursementBatchDetailsProps,
  UpdateDisbursementBatchProps,
  DisbursementBatchProgress,
  GetDisbursementBatchProgressProps,
  Disbursement,
  DisbursementEnhanced,
  MarkDisbursementAsPaidProps,
  GetDisbursementsInBatchListProps,
  GetDisbursementDonationsProps,
  LogReturnedPaymentProps,
  BeneficiaryDetails,
  BulkMarkAsPaidResponse,
  BulkDisbursementsResponse
} from './disbursements.types'

export const disbursementsService = ({ percentClient }: disbursementsServiceCapabilities) => ({
  getDisbursementBatchesList: (params: GetDisbursementBatchesListProps) =>
    percentClient.get<PBPListResponse<DisbursementBatch>>('v1/admin/disbursement-batches', { params }),
  createDisbursementBatch: (params: CreateDisbursementBatchProps) =>
    percentClient.post('/v1/admin/disbursement-batches', { ...params }),
  getDisbursementBatchDetails: ({ id }: GetDisbursementBatchDetailsProps) =>
    percentClient.get<PBPGetResponse<DisbursementBatch>>(`v1/admin/disbursement-batches/${id}`),
  updateDisbursementBatch: ({ id, name }: UpdateDisbursementBatchProps) =>
    percentClient.patch<PBPGetResponse<DisbursementBatch>>(`v1/admin/disbursement-batches/${id}`, { name }),
  getDisbursementBatchProgress: ({ id }: GetDisbursementBatchProgressProps) =>
    percentClient.get<PBPGetResponse<DisbursementBatchProgress>>(`v1/admin/disbursement-batches/${id}/progress`),
  getDisbursementBatchEnhancedDisbursementsList:
    (disbursementBatchId: string) => async (params: GetDisbursementsInBatchListProps) => {
      const disbursementBatchDisbursements = await percentClient.get<PBPListResponse<Disbursement>>(
        `v1/admin/disbursement-batches/${disbursementBatchId}/disbursements`,
        { params }
      )

      if (disbursementBatchDisbursements.data.data.length === 0 || disbursementBatchDisbursements.status !== 200) {
        return disbursementBatchDisbursements as AxiosResponse<PBPListResponse<DisbursementEnhanced>>
      }
      const organisationIds = Array.from(
        new Set(disbursementBatchDisbursements.data.data.map(({ organisationId }) => organisationId))
      )

      const partnerIds = Array.from(new Set(disbursementBatchDisbursements.data.data.map(({ partnerId }) => partnerId)))

      const organisationResponses = await Promise.all(
        chunk(organisationIds, 20).map(orgChunk =>
          percentClient.get<PBPListResponse<Organisation>>(`v1/admin/organisations`, {
            params: { organisationIds: orgChunk }
          })
        )
      )

      const partners: Partner[] = []

      if (partnerIds.length > 0) {
        const chunkedPartnerResponses = await Promise.all(
          chunk(partnerIds, 25).map(partnerChunk =>
            percentClient.get<PBPListResponse<Partner>>(`v1/admin/partners`, {
              params: { partnerIds: partnerChunk }
            })
          )
        )

        partners.push(...chunkedPartnerResponses.flatMap(response => response.data.data))
      }

      const invalidResponse = organisationResponses.find(org => org.status !== 200)

      if (invalidResponse) {
        return invalidResponse as unknown as AxiosResponse<PBPListResponse<DisbursementEnhanced>>
      }

      const organisationsMap = new Map(
        organisationResponses
          .flatMap(orgResponse => orgResponse.data.data)
          .map(({ id, name, countryCode }) => [
            id,
            {
              organisationName: name,
              organisationCountryCode: countryCode
            }
          ])
      )

      const partnersMap = new Map(partners.map(({ id, name }) => [id, { partnerName: name }]))

      return {
        ...disbursementBatchDisbursements,
        data: {
          ...disbursementBatchDisbursements.data,
          data: disbursementBatchDisbursements.data.data.map(props => ({
            ...props,
            organisationName: organisationsMap.get(props.organisationId)!.organisationName,
            organisationCountryCode: organisationsMap.get(props.organisationId)!.organisationCountryCode,
            partnerName: props.partnerId ? partnersMap.get(props.partnerId)?.partnerName : undefined,
            hasActivePaymentMethod: props.paymentMethod != null
          }))
        }
      }
    },
  markDisbursementAsPaid: ({ id, ...rest }: MarkDisbursementAsPaidProps) =>
    percentClient.post(`/v1/admin/disbursements/${id}/paid`, { ...rest }),
  bulkMarkAsPaidFromCsv: (csv: string, dryRun?: boolean) =>
    percentClient.post<BulkMarkAsPaidResponse>('/v1/admin/disbursements/bulk-mark-as-paid', { csv, dryRun }),
  cancelDisbursement: (id: string, reason?: string) =>
    percentClient.post(`v1/admin/disbursements/${id}/cancel`, reason ? { cancellationReason: reason } : undefined),
  cancelDisbursementBatch: (id: string) =>
    percentClient.post<PBPGetResponse<DisbursementBatchWithCancellationFailures>>(
      `v1/admin/disbursement-batches/${id}/cancel`
    ),
  trusteeApproveBatch: (id: string) =>
    percentClient.post<PBPGetResponse<DisbursementBatch>>(`v1/admin/disbursement-batches/${id}/trustee-approve`),
  agentApproveBatch: (id: string) =>
    percentClient.post<PBPGetResponse<DisbursementBatch>>(`v1/admin/disbursement-batches/${id}/agent-approve`),
  managerApproveBatch: (id: string) =>
    percentClient.post<PBPGetResponse<DisbursementBatch>>(`v1/admin/disbursement-batches/${id}/manager-approve`),
  markDisbursementAsApproved: (id: string) => percentClient.post(`v1/admin/disbursements/${id}/approve`),
  markDisbursementAsSent: (id: string) => percentClient.post(`v1/admin/disbursements/${id}/in-transit`),
  markApprovedChequeDisbursementsAsSent: (batchId: string) =>
    percentClient.post(`v1/admin/disbursement-batches/${batchId}/in-transit`),
  bulkApproveDisbursements: (disbursementIds: string[]) =>
    percentClient.post<PBPGetResponse<BulkDisbursementsResponse>>('v1/admin/disbursements/bulk-approve', {
      disbursementIds
    }),
  bulkMarkDisbursementsAsSent: (disbursementIds: string[]) =>
    percentClient.post<PBPGetResponse<BulkDisbursementsResponse>>('v1/admin/disbursements/bulk-in-transit', {
      disbursementIds
    }),
  bulkCancelDisbursements: (disbursementIds: string[], cancellationReason?: string) =>
    percentClient.post<PBPGetResponse<BulkDisbursementsResponse>>(
      'v1/admin/disbursements/bulk-cancel',
      cancellationReason ? { disbursementIds, cancellationReason } : { disbursementIds }
    ),
  getBeneficiaryDetails: (disbursementId: string) =>
    percentClient.get<PBPGetResponse<BeneficiaryDetails>>(`v1/admin/disbursements/${disbursementId}/beneficiary`),
  confirmBeneficiary: (disbursementId: string) =>
    percentClient.post(`v1/admin/disbursements/${disbursementId}/confirm-beneficiary`),
  logReturnedPayment: ({ disbursementId, reason }: LogReturnedPaymentProps) =>
    percentClient.post(`v1/admin/disbursements/${disbursementId}/log-return`, { reason }),
  getDisbursementDonations: async (
    params: GetDisbursementDonationsProps
  ): Promise<AxiosResponse<PBPListResponse<DonationEnhanced>>> => {
    const { disbursementId, ...pagination } = params

    const donations = await percentClient.get<PBPListResponse<Donation>>(
      `v1/admin/disbursements/${disbursementId}/donations`,
      {
        params: pagination
      }
    )

    if (donations.data?.data?.length === 0 || donations.status !== 200) {
      return donations as unknown as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }

    const partnerIds = Array.from(new Set(donations.data.data.map(({ partnerId }) => partnerId)))
    const organisationIds = Array.from(new Set(donations.data.data.map(({ organisationId }) => organisationId)))
    const [partners, organisations] = await Promise.all([
      percentClient.get<PBPListResponse<Partner>>('v1/admin/partners', { params: { partnerIds } }),
      percentClient.get<PBPListResponse<Organisation>>('v1/admin/organisations', { params: { organisationIds } })
    ])

    if (partners.status !== 200) {
      return partners as unknown as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }

    if (organisations.status !== 200) {
      return organisations as unknown as AxiosResponse<PBPListResponse<DonationEnhanced>>
    }
    const partnerMap = new Map(partners.data.data.map(props => [props.id, props]))
    const organisationsMap = new Map(organisations.data.data.map(props => [props.id, props]))

    return {
      ...donations,
      data: {
        ...donations.data,
        data: donations.data.data.map(props => ({
          ...props,
          partner: partnerMap.get(props.partnerId)!,
          organisation: organisationsMap.get(props.organisationId)!
        }))
      }
    }
  }
})
